import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Form, Input, Button, Space, Select, Typography, message } from 'antd';
import { EditOutlined, FilePdfOutlined, FileMarkdownOutlined, LinkOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import {
  updateKnowledgePdf,
  updateKnowledgeMarkdown,
  updateKnowledgeLink,
} from '@/api/knowledge';
import type {
  KnowledgePdf,
  KnowledgeMarkdown,
  KnowledgeLink,
  UnifiedSource,
} from '@/api/types';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Edit-source drawer — Description + Tags + (for links) URL editing for an
// existing PDF / Markdown / Link source. Built for clinical SMEs who want to
// add provenance notes or apply tags to an already-uploaded file without
// having to delete + re-upload (which loses the indexed citations).
//
// Per project convention: drawer over modal for richer multi-field flows.
// ─────────────────────────────────────────────────────────────────────────────

interface EditSourceDrawerProps {
  open: boolean;
  onClose: () => void;
  source: UnifiedSource | null;
  /** Called after a successful save so the parent can refetch the list. */
  onSaved: () => void;
}

interface FormValues {
  title?: string;
  description?: string;
  onlineLink?: string; // links only
  tags?: string[];
}

export const EditSourceDrawer = observer(function EditSourceDrawer({
  open,
  onClose,
  source,
  onSaved,
}: EditSourceDrawerProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);

  // Reset form when a new source opens. The source comes from the unified
  // table view so we have title/description/tags/url already; that lets us
  // skip a per-row GetById round-trip on common edits.
  useEffect(() => {
    if (open && source) {
      form.resetFields();
      form.setFieldsValue({
        title: source.title === '(untitled PDF)' || source.title === '(untitled Markdown)' || source.title === '(untitled link)'
          ? ''
          : source.title,
        description: source.description ?? '',
        onlineLink: source.kind === 'link' ? source.url ?? '' : undefined,
        tags: source.tags ?? [],
      });
    }
  }, [open, source, form]);

  if (!source) return null;

  const Icon =
    source.kind === 'pdf' ? FilePdfOutlined :
    source.kind === 'markdown' ? FileMarkdownOutlined :
    LinkOutlined;

  const accent =
    source.kind === 'pdf' ? colors.ink[600] :
    source.kind === 'markdown' ? colors.violet[600] :
    colors.amber[600];

  async function handleSubmit() {
    if (!source) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Build the full entity — the backend Update endpoints take the whole
      // record (not a patch), so we copy id + knowledgeId from the source row
      // and merge in the form values. Untouched fields stay null/undefined and
      // the backend manager preserves what was already on the entity.
      const baseFields = {
        id: source.id,
        knowledgeId: source.knowledgeId,
        title: values.title?.trim() || undefined,
        description: values.description?.trim() || undefined,
        tags: values.tags ?? [],
      };

      if (source.kind === 'pdf') {
        await updateKnowledgePdf(source.id, baseFields as KnowledgePdf);
      } else if (source.kind === 'markdown') {
        await updateKnowledgeMarkdown(source.id, baseFields as KnowledgeMarkdown);
      } else {
        // link — also include onlineLink
        await updateKnowledgeLink(source.id, {
          ...baseFields,
          onlineLink: values.onlineLink?.trim() || undefined,
        } as KnowledgeLink);
      }

      message.success(`Updated "${values.title || source.title}"`);
      trackEvent('Knowledge.SourceEdited', {
        kind: source.kind,
        sourceId: source.id,
        knowledgeId: source.knowledgeId,
      });
      onSaved();
      onClose();
    } catch (err: unknown) {
      const isFormError = err && typeof err === 'object' && 'errorFields' in err;
      if (!isFormError) {
        message.error(err instanceof Error ? err.message : 'Failed to save changes');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    if (!submitting) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      placement="right"
      width="40%"
      destroyOnClose
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* Header — matches the source's color tone (ink for PDF, violet for MD, amber for link) */}
      <div
        style={{
          padding: '20px 24px 18px',
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
          background: isDark
            ? `${accent}22`
            : `linear-gradient(135deg, ${accent}11, ${colors.slate[50]})`,
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Icon style={{ fontSize: 18, color: accent }} />
          <Text strong style={{ fontSize: 16 }}>
            Edit {source.kind === 'pdf' ? 'PDF' : source.kind === 'markdown' ? 'Markdown' : 'Link'} source
          </Text>
        </div>
        <Text type="secondary" style={{ display: 'block', fontSize: 12.5, lineHeight: 1.55 }}>
          Update metadata and tags. The file content {source.kind === 'link'
            ? '(URL) will be re-fetched and re-indexed.'
            : 'is unchanged — only metadata is saved. Use Replace File to swap content.'}
        </Text>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        <Form form={form} layout="vertical" preserve={false} requiredMark="optional">
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { max: 300, message: 'Up to 300 characters' },
            ]}
            extra="The user-facing name of this source. Changing it may break agent citations that reference the old title."
          >
            <Input placeholder="e.g. Diabetes Glucose Targets" maxLength={300} showCount />
          </Form.Item>

          {source.kind === 'link' && (
            <Form.Item
              name="onlineLink"
              label="URL"
              rules={[
                { required: true, message: 'A URL is required for link sources' },
                { type: 'url', message: 'Must be a valid URL (https://…)' },
                { max: 2048, message: 'URLs must be 2048 characters or fewer' },
              ]}
              extra="The page contents will be fetched and re-indexed when you save."
            >
              <Input placeholder="https://…" maxLength={2048} />
            </Form.Item>
          )}

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 2000, message: 'Up to 2000 characters' }]}
            extra="Provenance / context note. Up to 2000 characters — paste citation details, source date, or any context an agent author might need later."
          >
            <Input.TextArea
              placeholder="e.g. ASCO 2024 guidelines, p.42. Updated 2026-03 for new HbA1c targets."
              rows={5}
              maxLength={2000}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="tags"
            label="Tags"
            extra="Type a tag and press Enter. Tags drive role-based filtering during retrieval (e.g., scope a Test Query to nccn-2024 sources only)."
          >
            <Select
              mode="tags"
              placeholder="e.g., nccn-2024, diabetes, patient-education"
              tokenSeparators={[',']}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px 24px',
          borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            loading={submitting}
            onClick={handleSubmit}
          >
            Save changes
          </Button>
        </Space>
      </div>
    </Drawer>
  );
});
